import type { ComponentProps } from "react";
import { useId } from "react";
import { StarIcon } from "@heroicons/react/20/solid";
import { tv } from "tailwind-variants";

const STAR_COUNT = 5;
const STAR_SIZE = 20;
const STAR_GAP = 2;
const STEP = STAR_SIZE + STAR_GAP;
const WIDTH = STAR_COUNT * STAR_SIZE + (STAR_COUNT - 1) * STAR_GAP;

const starsVariants = tv({
  base: "inline-block h-4 w-auto shrink-0",
});

export type StarsProps = {
  rating: number;
} & Omit<ComponentProps<"svg">, "children">;

const renderStars = (fillClassName: string) => {
  return Array.from({ length: STAR_COUNT }, (_, index) => {
    return (
      <StarIcon
        className={fillClassName}
        height={STAR_SIZE}
        key={index}
        width={STAR_SIZE}
        x={index * STEP}
      />
    );
  });
};

export const Stars = ({ className, rating, ...props }: StarsProps) => {
  const clipId = `stars-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const clamped = Math.min(Math.max(rating, 0), STAR_COUNT);
  const fullStars = Math.floor(clamped);
  const filledWidth = fullStars * STEP + (clamped - fullStars) * STAR_SIZE;

  return (
    <svg
      aria-label={`Rated ${clamped.toFixed(1)} out of 5`}
      className={starsVariants({ className })}
      role="img"
      viewBox={`0 0 ${WIDTH} ${STAR_SIZE}`}
      {...props}
    >
      <defs>
        <clipPath id={clipId}>
          <rect height={STAR_SIZE} width={filledWidth} />
        </clipPath>
      </defs>
      {renderStars("fill-line")}
      <g clipPath={`url(#${clipId})`}>{renderStars("fill-content")}</g>
    </svg>
  );
};
